import { useMemo } from "react"
import { Autocomplete, TextField, ListSubheader, CircularProgress } from "@mui/material"
import { useTranslation } from "react-i18next"
import { Controller, useFormContext } from "react-hook-form"

import type { Tower, TowerOption } from "types/towers.types"
import { useAllPowerlineTowers } from "helpers/hooks/towers/useAllPowerlineTowers"
import { nameOrUnnamed } from "helpers/utils/translations"
import { useSpansContext } from "./useSpansContext"
import { getTowerGroupedOptions, isTowerPairFieldMissing } from "./helpers"

interface TowerAutocompleteProps {
  name: string
  placeholder?: string
  initialData?: Tower
}

const TowerAutocomplete = ({
  name,
  placeholder,
  initialData,
}: TowerAutocompleteProps) => {
  const { control, watch } = useFormContext()
  const { t } = useTranslation()

  const powerlineId = watch("powerlineId")
  const towerAId = watch("towerAId")
  const towerBId = watch("towerBId")

  const { towers: allTowers, isLoading: towersLoading } = useAllPowerlineTowers({
    powerlineId: powerlineId ? Number(powerlineId) : undefined,
  })

  const { spansByTowerPair } = useSpansContext()

  const otherTowerId = useMemo(() => {
    if (name === "towerAId") return towerBId
    if (name === "towerBId") return towerAId
    return undefined
  }, [name, towerAId, towerBId])

  const options = useMemo(
    () =>
      getTowerGroupedOptions({
        currentTower: initialData,
        otherTowerId,
        allTowers: allTowers || [],
        spansByTowerPair,
        towersLoading,
        t,
      }),
    [initialData, otherTowerId, allTowers, spansByTowerPair, towersLoading, t],
  )

  const initialOption: TowerOption | null = initialData
    ? {
        id: initialData.id,
        label: `${nameOrUnnamed(initialData.name, t)} (${initialData.id})`,
      }
    : null

  const disabled = !powerlineId
  const effectivePlaceholder = disabled
    ? t("device_installation.SELECT_POWERLINE_FIRST")
    : placeholder

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (_value, values) =>
          !isTowerPairFieldMissing(name, values) || t("generic.FIELD_REQUIRED"),
      }}
      render={({ field, fieldState: { error } }) => {
        const { onChange, value, ref } = field

        const selectedOption = (() => {
          if (!value) return null
          const found = (options || []).find((opt) => opt.id === value)
          if (found) return found
          if (initialOption && initialOption.id === value) {
            return initialOption
          }
          return { id: value as number, label: String(value) }
        })()

        const optionsWithInitial =
          initialOption &&
          initialOption.id === value &&
          !options.some((opt) => opt.id === initialOption.id)
            ? [initialOption, ...options]
            : options

        return (
          <Autocomplete
            options={optionsWithInitial || []}
            disabled={disabled}
            loading={towersLoading}
            value={selectedOption}
            onChange={(_event, newValue) => {
              onChange(newValue ? newValue.id : null)
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.label || ""}
            groupBy={(option) => option.group || ""}
            noOptionsText={
              towersLoading
                ? t("generic.LOADING")
                : !powerlineId
                  ? t("device_installation.SELECT_POWERLINE_FIRST")
                  : t("generic.NO_RESULTS_FOUND")
            }
            sx={{
              "& .MuiAutocomplete-listbox": {
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
            renderGroup={(params) => (
              <div key={params.key}>
                <ListSubheader
                  sx={{
                    typography: "caption",
                    fontStyle: "italic",
                    paddingY: "2px",
                  }}
                >
                  {params.group}
                </ListSubheader>
                <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
              </div>
            )}
            componentsProps={{
              popper: {
                sx: {
                  "& .MuiAutocomplete-paper": {
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref}
                variant="outlined"
                size="small"
                placeholder={effectivePlaceholder}
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 0 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {towersLoading ? (
                        <CircularProgress color="primary" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )
      }}
    />
  )
}

export default TowerAutocomplete
